import React, { useMemo, useCallback } from "react"
import { useParams } from "react-router-dom"

import { useTranslation } from "providers/i18n"
import { useAppUI } from "providers/ui"

import { getDurationDate } from "utils/date"

import {
  Box,
  Card,
  CardHeader,
  CardContent,
  CardMedia,
  List,
  Typography,
  useTheme,
  Stack,
  Button,
} from "@mui/material"
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import { size } from "lodash"

import { getIcon } from "utils/icon"
import { usePromotionDetail } from "hooks/promotion/usePromotionDetail"
import { useMobileUrl } from "hooks/useMobileUrl"
import { useImageModal } from "hooks/imageModal/useImageModal"

import { AppNavigation as DefaultAppNavigation } from "containers/kiosk-v"

import { LocationIcon, PercentIcon } from "components/icons"
import { SearchToggleButton } from "components/domains/search"
import { Head } from "components/Head"
import { InfoListItem } from "components/ListItem"
import { LocationInfo } from "components/domains/features/LocationInfo"
import { QRCode } from "components/QRCode"
import { Carousel, CarouselItem } from "components/Carousel"
import { ImageCarouselModal } from "components/Image"
import { LanguageButton } from "extensions/iconsiam/components/language/LanguageButton"
import { EmptyData } from "components/EmptyData"
import { LeftToRightWrapper } from "components/LeftToRightWrapper"

export const PromotionDetailKioskVPage = ({
  Layout = Box,
  AppNavigation = DefaultAppNavigation,
  additionalMenu = null,
}) => {
  const params = useParams()
  const { handleImage, openModal, activeImage, handleClose } = useImageModal()
  const { promotionId } = params
  const { promotion, navigateToLocation } = usePromotionDetail(promotionId)
  const { goBack, openSearch } = useAppUI()
  const { mobileUrl } = useMobileUrl()

  const theme = useTheme()
  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.promotions) || <PercentIcon />,
    [theme]
  )

  const calendarIcon = useMemo(
    () => getIcon(theme?.assets?.calendarIcon) || <CalendarTodayIcon />,
    [theme]
  )

  const locationIcon = useMemo(
    () => getIcon(theme?.assets?.locationIcon) || <LocationIcon />,
    [theme]
  )

  const styleOverrides = theme.components[`VuiDetailCard`]?.styleOverrides || {}
  const {
    card,
    infoListItem,
    cardContentWrapper,
    cardContent,
    cardHeader,
    cardContentPrimaryText,
    cardContentSecondaryText,
    navigationWrapper,
    infoContainer,
    qrCodeContainer,
    qrCode,
    qrcodePrimaryText,
    qrcodeSecondaryText,
    buttonContainer,
    button,
    cardContentTextWrapper,
    cardMediaWrapper,
    galleryCarouselWrapper,
    galleryCarousel,
    galleryCarouselItem,
    galleryCarouselModal,
    toolbarContainer,
    actionButton,
    actionContainer,
    searchBarContainer,
    emptyDataContainer,
  } = styleOverrides
  const { galleryCarouselOption } =
    theme.components[`PromotionDetailPage`]?.defaultProps || {}

  const { t } = useTranslation()

  const handleClickBack = useCallback(() => {
    goBack()
  }, [goBack])

  const allImagesUrls = useMemo(
    () =>
      (size(promotion?.allImages) > 0
        ? promotion?.allImages?.map((v) => v?.url)
        : []) as string[],
    [promotion?.allImages]
  )

  return (
    <>
      <Head
        title={`${t(promotion?.name || "Unknown")} (${t("Promotion Detail")})`}
      />
      <Layout>
        {!promotion ? (
          <Box sx={emptyDataContainer}>
            <EmptyData
              icon={emptyIcon}
              primary={t("No Current Promotions")}
              secondary={t("Check Back Soon")}
            />
          </Box>
        ) : (
          <Card
            elevation={0}
            sx={card}
            className={!promotion.descriptionText ? "no-description" : ""}
          >
            <Box sx={cardMediaWrapper}>
              {promotion.allImages && (
                <Box sx={galleryCarouselWrapper}>
                  <LeftToRightWrapper>
                    <Carousel
                      options={{
                        type: "fade",
                        padding: 0,
                        gap: 8,
                        pagination: true,
                        autoplay: true,
                        ...galleryCarouselOption,
                      }}
                      sx={galleryCarousel}
                    >
                      {promotion.allImages.map(({ id, url }) => {
                        return (
                          <CarouselItem
                            key={`slide-${id}`}
                            sx={galleryCarouselItem}
                          >
                            <CardMedia
                              component="img"
                              sx={galleryCarouselItem}
                              src={url as string}
                              onClick={() => handleImage(url)}
                            />
                          </CarouselItem>
                        )
                      })}
                    </Carousel>
                  </LeftToRightWrapper>
                </Box>
              )}
            </Box>
            <Box sx={cardContentWrapper}>
              <CardHeader title={t(promotion.name)} sx={cardHeader} />
              {promotion.showInfo && (
                <List sx={infoContainer}>
                  {!promotion.showAlways && (
                    <InfoListItem
                      sx={infoListItem}
                      icon={calendarIcon}
                      primary={getDurationDate(
                        promotion.startDate,
                        promotion.endDate
                      )}
                      secondary={promotion.openingText}
                      direction="ltr"
                    />
                  )}
                  {promotion.hasLocation && (
                    <InfoListItem
                      sx={infoListItem}
                      icon={locationIcon}
                      primary={
                        <LocationInfo
                          feature={promotion.feature}
                          component="span"
                        />
                      }
                      secondary={t(promotion.boothName)}
                    />
                  )}
                </List>
              )}
              <CardContent
                sx={cardContent}
                className="VuiDetailCard-cardContent"
              >
                <Box sx={cardContentTextWrapper}>
                  <Typography
                    variant="body1"
                    gutterBottom
                    sx={cardContentPrimaryText}
                  >
                    {t("Promotion Detail")}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={cardContentSecondaryText}
                    dangerouslySetInnerHTML={{
                      __html: promotion.descriptionText,
                    }}
                  ></Typography>
                </Box>
              </CardContent>
              {mobileUrl && (
                <Box sx={qrCodeContainer}>
                  <QRCode sx={qrCode} url={mobileUrl} />
                  <Stack>
                    <Typography
                      variant="subtitle2"
                      component="p"
                      sx={qrcodePrimaryText}
                    >
                      {t("Scan Here")}
                    </Typography>
                    <Typography
                      variant="caption"
                      component="p"
                      sx={qrcodeSecondaryText}
                    >
                      {t("For Mobile")}
                    </Typography>
                  </Stack>
                </Box>
              )}
            </Box>
          </Card>
        )}
        <Box key="app-navigation" sx={navigationWrapper}>
          <Box sx={buttonContainer}>
            <Button variant="outlined" sx={button} onClick={handleClickBack}>
              {t("Back")}
            </Button>
            {promotion?.hasLocation && (
              <Button
                variant="contained"
                sx={button}
                className="highlight"
                onClick={navigateToLocation}
              >
                {t("View Location")}
              </Button>
            )}
          </Box>
          <Box sx={toolbarContainer}>
            <Box sx={actionContainer}>
              <LanguageButton sx={actionButton} />
              {additionalMenu}
            </Box>
            <Box sx={searchBarContainer}>
              <SearchToggleButton
                placeholder={t("Search shops and more...")}
                onClick={openSearch}
              />
            </Box>
          </Box>
          {AppNavigation && <AppNavigation />}
        </Box>
      </Layout>
      <ImageCarouselModal
        className="VuiDetailCard-galleryCarouselModal--promotion"
        sx={galleryCarouselModal}
        activeSrc={activeImage}
        open={openModal}
        imageSrcs={allImagesUrls}
        onClose={handleClose}
        carouselOptions={{
          rewind: false,
        }}
      />
    </>
  )
}
